/**
 * Shared shape of the yearly VAT report: the same seven columns the server-rendered
 * comparative PDF prints, in the same right-to-left order.
 */
export const VAT_COLUMNS = [
    { key: 'incomesNoZeroVats', labelKey: 'ExemptTransactions' },
    { key: 'incomesNoVat', labelKey: 'TaxableTransactions' },
    { key: 'vatTransactions', labelKey: 'TransactionsVAT' },
    { key: 'equipmentVAT', labelKey: 'EquipmentInputsVAT' },
    { key: 'otherVATTaxes', labelKey: 'OtherInputsVAT' },
    { key: 'netVAT', labelKey: 'VATToPay' },
];

/** The editable subset — "לתשלום" is always derived, never typed. */
export const MANUAL_COLUMNS = VAT_COLUMNS.filter((column) => column.key !== 'netVAT');

export const EMPTY_VALUES = {
    incomesNoZeroVats: 0,
    incomesNoVat: 0,
    vatTransactions: 0,
    equipmentVAT: 0,
    otherVATTaxes: 0,
    netVAT: 0,
};

export const toNumber = (value) => {
    if (value === '' || value === null || value === undefined) return 0;
    const parsed = Number(String(value).replace(/,/g, ''));
    return Number.isNaN(parsed) ? 0 : parsed;
};

export const calculateNetVat = (values) =>
    toNumber(values?.vatTransactions) - toNumber(values?.equipmentVAT) - toNumber(values?.otherVATTaxes);

/**
 * Stable identity of a period across payloads: the year and month it starts in.
 * Period labels cannot be compared directly because a bi-monthly period of the current
 * year is truncated at the current month ("05/2025 - 05/2025") while a whole-year grid
 * spells out the full span ("05/2025 - 06/2025").
 */
export const periodMatchKey = (isoDate) => {
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const toIsoDate = (year, month, day) =>
    `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const lastDayOfMonth = (year, month) => new Date(year, month, 0).getDate();

/**
 * Normalizes a period to the full span its reporting type requires, so saving passes the
 * server's monthly / bi-monthly period validation even for the still-open current period.
 */
const normalizePeriodDates = (isoStartDate, isBiMonthly) => {
    const start = new Date(isoStartDate);
    const startYear = start.getFullYear();
    const startMonth = start.getMonth() + 1;

    const monthSpan = isBiMonthly ? 2 : 1;
    const endMonthIndex = startMonth + monthSpan - 1;
    const endYear = startYear + Math.floor((endMonthIndex - 1) / 12);
    const endMonth = ((endMonthIndex - 1) % 12) + 1;

    return {
        startDate: toIsoDate(startYear, startMonth, 1),
        endDate: toIsoDate(endYear, endMonth, lastDayOfMonth(endYear, endMonth)),
    };
};

/** Builds the editable rows for a year, seeded with whatever is already reported. */
export const buildManualRows = (data) =>
    (data?.periods ?? []).map((period) => ({
        periodKey: period.periodKey,
        matchKey: periodMatchKey(period.startDate),
        ...normalizePeriodDates(period.startDate, data?.isBiMonthly),
        isReported: period.isReported,
        original: { ...EMPTY_VALUES, ...period.reported },
        values: {
            incomesNoZeroVats: period.reported?.incomesNoZeroVats ?? 0,
            incomesNoVat: period.reported?.incomesNoVat ?? 0,
            vatTransactions: period.reported?.vatTransactions ?? 0,
            equipmentVAT: period.reported?.equipmentVAT ?? 0,
            otherVATTaxes: period.reported?.otherVATTaxes ?? 0,
        },
        selected: false,
        filledFromDocument: false,
    }));

export const isRowDirty = (row) =>
    MANUAL_COLUMNS.some((column) => toNumber(row.values[column.key]) !== toNumber(row.original[column.key]));

/** Maps an editable row to the payload the CreateVatBulk endpoint expects. */
export const toBulkPeriodPayload = (row) => ({
    startDate: row.startDate,
    endDate: row.endDate,
    VATTransactions: toNumber(row.values.vatTransactions),
    OtherVATTaxes: toNumber(row.values.otherVATTaxes),
    EquipmentVAT: toNumber(row.values.equipmentVAT),
    NetVAT: calculateNetVat(row.values),
    IncomesNoVat: toNumber(row.values.incomesNoVat),
    IncomesNoZeroVats: toNumber(row.values.incomesNoZeroVats),
    ExpensesNoVat: 0,
    EquipmentExpenses: 0,
    delete: false,
});

export const toBulkDeletePayload = (row) => ({
    startDate: row.startDate,
    endDate: row.endDate,
    delete: true,
});

export const yearOptions = (span = 8) => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: span }, (_, index) => currentYear - index);
};
